import { z } from "zod";

/**
 * Zod schemas + TS types for the post-checkout onboarding tour (PREC-3700).
 * Mirrors apps/api/src/onboarding-tour/dto + enums.
 */

/** Audience flavor of the tour — derived server-side once. */
export enum OnboardingTourVariant {
	SELF_SIGNUP = "SELF_SIGNUP",
	DEMO_FIRST = "DEMO_FIRST",
	INVITED = "INVITED",
}

/**
 * Lifecycle of a user's tour. PREC-3719 retired HIDDEN ("never show again") —
 * leaving the flow is always temporary now.
 */
export enum OnboardingTourStatus {
	ACTIVE = "ACTIVE",
	SKIPPED = "SKIPPED",
	DONE = "DONE",
}

/**
 * Every tour step id the API may send. SCORING is the retired one-step scoring
 * walkthrough, replaced by the five SCORING_* steps of the simplified setup; its
 * id stays so persisted rows keep resolving.
 */
export enum TourStepId {
	HOW_IT_WORKS = "HOW_IT_WORKS",
	SCORING = "SCORING",
	SCORING_INTRO = "SCORING_INTRO",
	SCORING_SEGMENTS = "SCORING_SEGMENTS",
	SCORING_ROLES = "SCORING_ROLES",
	SCORING_GRADING = "SCORING_GRADING",
	SCORING_ACTIVATE = "SCORING_ACTIVATE",
	CREATE_KNOWLEDGE = "CREATE_KNOWLEDGE",
	CONNECT = "CONNECT",
	EXTENSION = "EXTENSION",
	LANGUAGE = "LANGUAGE",
	DEEP_RESEARCH = "DEEP_RESEARCH",
	KNOWLEDGE = "KNOWLEDGE",
	COPYWRITING = "COPYWRITING",
}

/**
 * Async state of a step's generation (PREC-3719 §5.1) — drives the rail pip and
 * the "→ Review" affordance. Priority: DONE > READY > RUNNING > FAILED > IDLE.
 * FAILED (PREC-4174): the latest generation job failed with nothing to review.
 */
export enum OnboardingTourStepStatus {
	IDLE = "IDLE",
	RUNNING = "RUNNING",
	READY = "READY",
	DONE = "DONE",
	FAILED = "FAILED",
}

/** Stages of the lead-list tour (PREC-3719 §8) — the explanation flow. */
export enum OnboardingListTourStage {
	LEAD_LISTS = "LEAD_LISTS",
	WORKING_IN_LISTS = "WORKING_IN_LISTS",
	ENRICHMENT_BUCKETS = "ENRICHMENT_BUCKETS",
	SCORING_IN_ACTION = "SCORING_IN_ACTION",
	OUTREACH = "OUTREACH",
	CAMPAIGN_BUILDER = "CAMPAIGN_BUILDER",
}

/**
 * Resume position inside a scoring step that walks several screens (mirrors the
 * setup-guide sub-stepper). Values are unique across steps, so one field
 * restores whichever scoring step the user is parked on: SCORING_SEGMENTS owns
 * CUSTOMER_GROUPS/PARTNERS/EXTRAS, SCORING_GRADING owns GRADING/CEO,
 * SCORING_ACTIVATE owns FEEDBACK_DEMO/CHECK.
 */
export enum OnboardingTourScoringStage {
	CUSTOMER_GROUPS = "CUSTOMER_GROUPS",
	PARTNERS = "PARTNERS",
	EXTRAS = "EXTRAS",
	GRADING = "GRADING",
	CEO = "CEO",
	FEEDBACK_DEMO = "FEEDBACK_DEMO",
	CHECK = "CHECK",
}

export const OnboardingTourReadinessSchema = z.object({
	scoringGenerated: z.boolean(),
	enrichmentDone: z.boolean(),
	scoredLeadsCount: z.coerce.number(),
	deepResearchPromptReady: z.boolean(),
	deepResearchResponseCount: z.coerce.number(),
	knowledgeDbReady: z.boolean(),
});

export type OnboardingTourReadiness = z.infer<typeof OnboardingTourReadinessSchema>;

export const OnboardingTourConnectSchema = z.object({
	linkedinConnected: z.boolean(),
	emailConnected: z.boolean(),
	seatAvailable: z.boolean(),
	isSendingUser: z.boolean(),
	// Optional so a web build that ships ahead of the API still parses — an
	// absent flag just keeps the EXTENSION step visible.
	extensionInstalled: z.boolean().optional(),
});

export type OnboardingTourConnect = z.infer<typeof OnboardingTourConnectSchema>;

export const OnboardingTourStateSchema = z.object({
	variant: z.enum(OnboardingTourVariant),
	status: z.enum(OnboardingTourStatus),
	currentStep: z.enum(TourStepId),
	completedSteps: z.array(z.enum(TourStepId)),
	scoringStage: z.enum(OnboardingTourScoringStage).nullable(),
	// Optional so older/partial payloads and test fixtures still parse; the API
	// always sends it. Consumers default a missing step to IDLE.
	stepStatus: z.record(z.enum(TourStepId), z.enum(OnboardingTourStepStatus)).optional(),
	// ISO timestamp a RUNNING step's generation was scheduled (nudge age, §5.3).
	// Only RUNNING steps appear, so keys are loose (a partial map by step id).
	stepRunningSince: z.record(z.string(), z.string()).optional(),
	// When the wait-or-explore nudge was last shown, or null.
	nudgeShownAt: z.string().nullish(),
	listTourStage: z.enum(OnboardingListTourStage).nullish(),
	listTourCompletedAt: z.string().nullish(),
	isCompanyAdmin: z.boolean(),
	readiness: OnboardingTourReadinessSchema,
	connect: OnboardingTourConnectSchema,
	// Paused lists that start once this guide reaches "Check & activate" (PREC-4098, PREC-4490);
	// 0 once they were started, and for a guide that never starts them.
	listsAwaitingGuide: z.coerce.number().optional(),
});

export type OnboardingTourState = z.infer<typeof OnboardingTourStateSchema>;

export const UpdateOnboardingTourProgressSchema = z.object({
	currentStep: z.enum(TourStepId).optional(),
	completedSteps: z.array(z.enum(TourStepId)).optional(),
	scoringStage: z.enum(OnboardingTourScoringStage).optional(),
});

export type UpdateOnboardingTourProgressRequest = z.infer<
	typeof UpdateOnboardingTourProgressSchema
>;

export const UpdateListTourSchema = z.object({
	stage: z.enum(OnboardingListTourStage).optional(),
	completed: z.boolean().optional(),
});

export type UpdateListTourRequest = z.infer<typeof UpdateListTourSchema>;
